import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import {
  REPORT_PLAYLIST_COLUMN_CUSTOM_FIELD_TOGGLE,
  REPORT_PLAYLIST_COLUMN_TOGGLE,
  REPORT_PLAYLIST_FILTER_TOGGLE,
  REPORT_PLAYLIST_GENERATE,
  REPORT_PLAYLIST_GENERATE_ERROR,
  REPORT_PLAYLIST_GENERATE_SUCCESS,
  REPORT_PLAYLIST_PARAMS_RESET,
  REPORT_PLAYLIST_PARAMS_UPDATE,
  REPORT_PLAYLIST_SECTION_TOGGLE,
} from '@disco/constants/ActionTypes'
import { buildPlaylistReportQuery } from '@disco/utils/report'
/**
 * Resets playlist report parameters to their default values.
 * @returns Action object to reset playlist report parameters
 */
export function resetPlaylistReportParams(): Thunk {
  return {
    type: REPORT_PLAYLIST_PARAMS_RESET,
  }
}
/**
 * Toggles a specific filter in the playlist report configuration.
 * @param key - The filter key to toggle
 * @param value - The new boolean value for the filter
 * @returns Action object to toggle the playlist filter
 */
export function togglePlaylistFilter(key: string, value: boolean): Thunk {
  return {
    type: REPORT_PLAYLIST_FILTER_TOGGLE,
    key,
    value,
  }
}
/**
 * Toggles visibility of a specific section in the playlist report.
 * @param key - The section key to toggle
 * @param value - The new boolean value for section visibility
 * @returns Action object to toggle the playlist section
 */
export function togglePlaylistSection(key: string, value: boolean): Thunk {
  return {
    type: REPORT_PLAYLIST_SECTION_TOGGLE,
    key,
    value,
  }
}
/**
 * Toggles visibility of a specific column in the report output.
 * @param key - The column key to toggle
 * @param value - The new boolean value for column visibility
 * @returns Action object to toggle the report column
 */
export function toggleReportColumn(key: string, value: boolean): Thunk {
  return {
    type: REPORT_PLAYLIST_COLUMN_TOGGLE,
    key,
    value,
  }
}
/**
 * Updates multiple playlist report parameters at once.
 * @param fields - Object containing field updates for report parameters
 * @returns Action object to update playlist report parameters
 */
export function updatePlaylistReportParams(fields: unknown): Thunk {
  return {
    type: REPORT_PLAYLIST_PARAMS_UPDATE,
    fields,
  }
}
/**
 * Generates a playlist report in the specified format.
 * @param format - The output format for the report (e.g., 'json', 'csv')
 * @returns Thunk that generates the report and dispatches success/error actions
 */
export function getPlaylistReport(format: string): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: REPORT_PLAYLIST_GENERATE,
    })

    try {
      const query = buildPlaylistReportQuery(
        getState().reports.playlistReport,
        format,
      )
      const results = await api.getPlaylistReport(query)
      dispatch({
        type: REPORT_PLAYLIST_GENERATE_SUCCESS,
        results: {
          data: results,
        },
        meta: {
          mixpanel: {
            event: 'Generate playlist report',
          },
        },
      })
    } catch (error) {
      dispatch({
        type: REPORT_PLAYLIST_GENERATE_ERROR,
        error,
      })
    }
  }
}
/**
 * Downloads a playlist report in the specified format.
 * Handles both immediate downloads and deferred processing for large reports.
 * @param format - The download format (e.g., 'deferred_csv')
 * @returns Thunk that initiates download and handles deferred processing
 */
export function downloadPlaylistReport(format: string): Thunk {
  return async (dispatch, getState) => {
    let event = ''

    switch (format) {
      case 'deferred_csv':
        event = 'Generate csv of playlist report'
        break
      case 'deferred_xlsx':
        event = 'Generate xlsx of playlist report'
        break
    }

    dispatch({
      type: REPORT_PLAYLIST_GENERATE,
      meta: {
        mixpanel: {
          event,
        },
      },
    })

    try {
      const query = buildPlaylistReportQuery(
        getState().reports.playlistReport,
        format,
      )
      const res = await api.getPlaylistReport(query)

      if (!(res.download_url || res.deferred)) {
        return dispatch({
          type: REPORT_PLAYLIST_GENERATE_ERROR,
          serverResponse: res,
          error: new Error('Unexpected server response'),
        })
      }

      const { deferred, download_url } = res
      let rowCount: number | null = null

      if (
        res.additional_info &&
        typeof res.additional_info.results === 'number'
      ) {
        rowCount = res.additional_info.results
      }

      let messageText

      if (deferred) {
        messageText = 'REPORT_GENERATING'
      } else {
        messageText = 'REPORT_DOWNLOADING'
      }

      return dispatch({
        type: REPORT_PLAYLIST_GENERATE_SUCCESS,
        results: {
          text: messageText,
          downloadUrl: download_url,
          rowCount,
        },
      })
    } catch (error) {
      dispatch({
        type: REPORT_PLAYLIST_GENERATE_ERROR,
        error,
      })
    }
  }
}
/**
 * Toggles visibility of custom field columns in the report output.
 * @param key - The custom field column key to toggle
 * @param value - The new boolean value for custom field column visibility
 * @returns Action object to toggle the custom field column
 */
export function toggleReportColumnCustomFields(
  key: string,
  value: boolean,
): Thunk {
  return {
    type: REPORT_PLAYLIST_COLUMN_CUSTOM_FIELD_TOGGLE,
    key,
    value,
  }
}
